import React, { useEffect, useState } from 'react';
import { Card, Form, Input, DatePicker, Select, Space, Row, Col, message, Radio, Table } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PersonnelSelect from '../../../../../components/modals/PersonnelSelect';
import WPermitSelect from '../../../../../components/modals/WPermitSelect';
import { WeldSelectModal } from '../../../../../components/modals';
import type { WeldSelectValue } from '../../../../../components/modals/WeldSelectModal';
import { useGetPersonnel } from '../../../../../hooks/genericHooks';
import { insertData, fetchSqlData, updateData } from '../../../../../services/api';
import { taskService } from '../../../../../components/common/TaskManagement/taskService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import FileUploadTool, { FileUploadToolHandle } from '../../../../../components/common/FileUploadTool';
import Button from '../../../../../components/common/Button';

const { TextArea } = Input;
const { Option } = Select;

const inspTypes = ['BC','VC','PT','RT','MT','UT','PMI','PWHT'] as const;

type InspType = typeof inspTypes[number];

const QaqcPipeRfiRequest: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [persModalOpen, setPersModalOpen] = useState(false);
  const [recModalOpen, setRecModalOpen] = useState(false);
  const [wpModalOpen, setWpModalOpen] = useState(false);
  const [weldModalOpen, setWeldModalOpen] = useState(false);
  const [weldModalMode, setWeldModalMode] = useState<'add' | 'remove'>('add');
  const [selectedWelds, setSelectedWelds] = useState<WeldSelectValue[]>([]);
  const [selectedWeldDetails, setSelectedWeldDetails] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const displayWeldRows = React.useMemo(() => {
    const detailById = new Map<string, any>();
    (selectedWeldDetails || []).forEach((d: any) => detailById.set(String(d.weldingdata_id), d));
    return (selectedWelds || []).map((w) => {
      const id = String(w.weldingdata_id);
      const d = detailById.get(id);
      return { weldingdata_id: id, weld_no_rev: w.weld_no_rev, ...(d || {}) };
    }).map((r) => ({ ...r, key: `${String(r.weldingdata_id)}|${String(r.weld_no_rev ?? '')}` }));
  }, [selectedWelds, selectedWeldDetails]);
  // Prune selection when data changes
  useEffect(() => {
    const validKeys = new Set((displayWeldRows || []).map(r => r.key));
    setSelectedRowKeys(prev => prev.filter(k => validKeys.has(String(k))));
  }, [displayWeldRows]);
  const { data: personnelData } = useGetPersonnel();
  const fileUploadRef = React.useRef<FileUploadToolHandle>(null);

  // Prefill Issued By from current user
  useEffect(() => {
    const currentPid = localStorage.getItem('personnel_id') || '';
    if (!currentPid) return;
    const list = (personnelData?.data || []) as any[];
    if (!Array.isArray(list) || list.length === 0) return;
    const me = list.find((p: any) => String(p.pers_id || '') === currentPid || String(p.id || '') === currentPid);
    const fullName = me ? `${me.first_name || ''} ${me.last_name || ''}`.trim() : undefined;
    form.setFieldsValue({
      issued_by: fullName || form.getFieldValue('issued_by'),
      issued_by_id: currentPid || form.getFieldValue('issued_by_id'),
    });
  }, [personnelData, form]);

  const onFinish = async (values: any) => {
    // Minimal validations
    if (!values.inspection_type) { message.warning('Select inspection type'); return; }
    if (!Array.isArray(selectedWelds) || selectedWelds.length === 0) { message.warning('Select at least one weld'); return; }
    if (!values.recipients_display) { message.warning('Select at least one recipient'); return; }

    // Ensure issuer is included among recipient_ids as a safety net
    const issuerPid: string | undefined = values.issued_by_id || undefined;
    const idsSet = new Set<string>(Array.isArray(values.recipient_ids) ? values.recipient_ids : []);
    if (issuerPid) idsSet.add(String(issuerPid));
    const recipient_ids = Array.from(idsSet);

    const weldingdata_ids = selectedWelds.map(w => w.weldingdata_id);
    const weld_no_revs = selectedWelds.map(w => (w.weld_no_rev === null || w.weld_no_rev === undefined) ? null : Number(w.weld_no_rev));
    // Build aligned arrays for enriched weld info
    const detailById = new Map<string, any>();
    (selectedWeldDetails || []).forEach((d: any) => detailById.set(String(d.weldingdata_id), d));
    const pipe_sizes: (string | null)[] = [];
    const service_class_names: (string | null)[] = [];
    const spool_ids: (number | null)[] = [];
    const weld_categories: (string | null)[] = [];
    const code_nos: (string | null)[] = [];
    const weldtype_names: (string | null)[] = [];
    const fluids: (string | null)[] = [];
    selectedWelds.forEach(w => {
      const id = String(w.weldingdata_id);
      const d = detailById.get(id) || {};
      const size = d.pipesize_name ?? d.pipesize ?? null;
      const svc = d.serviceclass_name ?? d.serviceclass ?? null;
      const spoolRaw = d.spool_id;
      const spool = (spoolRaw === null || spoolRaw === undefined || spoolRaw === '') ? null : Number(spoolRaw);
      const cat = d.weld_category ?? d.weld_category_name ?? null;
      const code = d.code_no ?? null;
      const wtype = d.weldtype_name ?? d.weldtype ?? null;
      pipe_sizes.push(size == null ? null : String(size));
      service_class_names.push(svc == null ? null : String(svc));
      spool_ids.push(Number.isFinite(spool as number) ? (spool as number) : null);
      weld_categories.push(cat == null ? null : String(cat));
      code_nos.push(code == null ? null : String(code));
      weldtype_names.push(wtype == null ? null : String(wtype));
      fluids.push(d.fluid == null ? null : String(d.fluid));
    });

    const payload: any = {
      issued_by: values.issued_by,
      request_date: values.request_date?.format?.('YYYY-MM-DD'),
      rfi_subject: values.rfi_subject,
      rfi_notes: values.rfi_notes,
      rfi_status: 'draft',
      recipients: values.recipients || [],
      recipient_ids,
      // Appointees/Invitees can be added later from edit UI if needed
      appointees: values.appointees || [],
      invitees: values.invitees || [],
      appointee_ids: values.appointee_ids || [],
      invitee_ids: values.invitee_ids || [],
      work_permit_ref_num: values.work_permit_ref_num || null,
      work_permit_description: values.work_permit_description || null,
      inspection_type: values.inspection_type as InspType,
      weldingdata_ids,
      weld_no_revs,
      upload_path: null,
      pipe_sizes,
      service_class_names,
      spool_ids,
      weld_categories,
      code_nos,
      weldtype_names,
      fluids,
    };

    // If files are staged to upload, ensure destination folder is configured for this route before inserting
    if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
      const cleanPath = (path: string) => {
        if (path.includes('/edit/')) {
          const segments = path.split('/');
          segments.pop();
          return segments.join('/');
        }
        return path;
      };
      const cleaned = cleanPath(location.pathname);
      try {
        const dest = await fetchSqlData('cor_destination_folders', 'route_path', cleaned);
        const rows = Array.isArray((dest as any)?.data) ? (dest as any).data : (Array.isArray(dest) ? dest : []);
        if (!Array.isArray(rows) || rows.length === 0) {
          message.error('No destination folder configured for this page. Please contact admin.');
          return;
        }
      } catch {
        message.error('Failed to verify destination folder. Please retry.');
        return;
      }
    }

    try {
      const res = await insertData('qaqc_pipe_rfi_forms', payload);
      message.success('Pipe RFI created');
      // Try to create notification tasks for recipients
      try {
        // Determine inserted id
        const returnedId = (res as any)?.inserted_id ?? (res as any)?.id ?? (Array.isArray((res as any)?.data) ? (res as any).data?.[0]?.id : undefined);
        const formRecordId = Number(returnedId);
        let formIdLabel: string | number | undefined = undefined;
        if (Number.isFinite(formRecordId)) {
          try {
            const fetchRes = await fetchSqlData('qaqc_pipe_rfi_forms', 'id', formRecordId);
            const createdRow = Array.isArray(fetchRes?.data) ? fetchRes.data[0] : (Array.isArray(fetchRes) ? fetchRes[0] : null);
            formIdLabel = createdRow?.form_id;
            // Finalize file uploads if any were staged
            if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
              const docRef = (createdRow?.form_id && String(createdRow.form_id)) || `RFI-${formRecordId}`;
              const up = await fileUploadRef.current.finalizeUpload(docRef);
              if (!up.success) {
                message.error(up.error || 'File upload failed');
                return; // abort further flow, leave record without attachments
              } else {
                try { await updateData('qaqc_pipe_rfi_forms', formRecordId, { upload_path: up.folderPath || null }); } catch {}
              }
            }
          } catch {}
        }
        const subj = values.rfi_subject || '';
        const descBase = `${subj} - ${values.issued_by || ''} - ${values.request_date?.format?.('YYYY-MM-DD') || ''}`.trim();
        const desc = (formIdLabel ? `Pipe RFI ${formIdLabel}: ` : 'Pipe RFI: ') + descBase;
        const navigateTo = Number.isFinite(formRecordId)
          ? `/qaqc/ndt/pipe_inspections/rfi/edit/${formRecordId}`
          : '/qaqc/ndt/pipe_inspections/rfi_list';

        if (Number.isFinite(formRecordId)) {
          await taskService.notify({
            entity: { kind: 'qaqc.pipe_rfi', id: formRecordId },
            assignees: recipient_ids,
            navigateTo,
            description: desc,
            tag: 'notify',
            clientKey: `pipe_rfi:${formRecordId}:notify`,
            fanoutPerAssignee: false,
          });
        } else {
          await taskService.notify({
            entity: { kind: 'qaqc.pipe_rfi', id: 'unknown' },
            assignees: recipient_ids,
            navigateTo,
            description: desc,
            tag: 'notify',
            fanoutPerAssignee: false,
          });
        }
      } catch {}
      try { window.dispatchEvent(new CustomEvent('qaqc_pipe_rfi_forms:changed')); } catch {}
      try { await queryClient.invalidateQueries({ queryKey: ['qaqc_pipe_rfi_forms'] }); } catch {}
      // Navigate back to list and request refresh
      navigate('/qaqc/ndt/pipe_inspections/rfi_list', { state: { refreshList: true } });
    } catch (e) {
      message.error('Failed to create Pipe RFI');
    }
  };

  return (
    <Card title="Pipe RFI Request" style={{ maxWidth: 720, margin: '0 auto' }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ request_date: dayjs() }}
        onFinish={onFinish}
        size="small"
      >
        {/* Hidden helpers */}
        <Form.Item name="issued_by_id" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="recipients" hidden>
          <Select mode="tags" open={false} style={{ display: 'none' }} />
        </Form.Item>
        <Form.Item name="recipient_ids" hidden>
          <Select mode="tags" open={false} style={{ display: 'none' }} />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label={<b>Issued By</b>} required style={{ marginBottom: 8 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="issued_by" noStyle rules={[{ required: true, message: 'Select issuer' }]}>
                  <Input readOnly placeholder="Select personnel" />
                </Form.Item>
                <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setPersModalOpen(true)}>Select</Button>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="request_date" label={<b>Request Date</b>} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={<b>Recipients (send to)</b>} required style={{ marginBottom: 8 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="recipients_display" noStyle rules={[{ required: true, message: 'Select at least one recipient' }]}>
              <TextArea readOnly placeholder="Select one or more personnel" autoSize={{ minRows: 1, maxRows: 6 }} />
            </Form.Item>
            <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setRecModalOpen(true)}>Select</Button>
          </Space.Compact>
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label={<b>Work Permit Ref No.</b>} style={{ marginBottom: 8 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="work_permit_ref_num" noStyle>
                  <Input readOnly placeholder="e.g. WP-2025-00123" />
                </Form.Item>
                <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setWpModalOpen(true)}>Select</Button>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              label={<b>Work Permit Description</b>}
              name="work_permit_description"
              style={{ marginBottom: 8 }}
            >
              <Input readOnly placeholder="Auto-filled from Work Permit selection (optional)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="inspection_type" label={<b>Inspection Type</b>} rules={[{ required: true, message: 'Select type' }]} style={{ marginBottom: 8 }}>
              <Radio.Group optionType="button" buttonStyle="solid">
                {inspTypes.map(t => (<Radio.Button key={t} value={t}>{t}</Radio.Button>))}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <b>Welds</b>
                    <span style={{ opacity: 0.8 }}>({selectedWelds.length})</span>
                  </span>
                  <span style={{ marginLeft: '20px', display: 'inline-flex', gap: 4 }}>
                    <Button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setWeldModalMode('add'); setWeldModalOpen(true); }}
                      icon={<PlusOutlined />}
                      style={{ color: '#1677ff' }}
                    />
                    <Button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (!selectedRowKeys.length) { message.info('Select welds to remove'); return; }
                        const removeIds = new Set(selectedRowKeys.map(k => String(k).split('|')[0]));
                        setSelectedWelds(prev => prev.filter(w => !removeIds.has(String(w.weldingdata_id))));
                        setSelectedWeldDetails(prev => prev.filter((w: any) => !removeIds.has(String(w.weldingdata_id))));
                        setSelectedRowKeys([]);
                      }}
                      icon={<MinusOutlined />}
                      style={{ color: '#f5222d' }}
                    />
                  </span>
                </div>
              }
              style={{ marginBottom: 8 }}
            >
              <Space.Compact style={{ width: '100%' }}>
                <Input readOnly value={`${selectedWelds.length} selected`} />
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginBottom: 12 }}>
          <style>{`
            .rfi-welds-table .ant-table-tbody > tr > td,
            .rfi-welds-table .ant-table-thead > tr > th { padding-top: 4px !important; padding-bottom: 4px !important; }
            .rfi-welds-table .ant-table-cell { white-space: nowrap; }
          `}</style>
          <Table
            className="rfi-welds-table"
            key={`welds-${selectedWelds.map((w: any) => `${String(w.weldingdata_id)}|${String(w.weld_no_rev ?? '')}`).join(',')}`}
            size="small"
            tableLayout="fixed"
            pagination={false}
            columns={[
              { title: 'Welding ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 210, ellipsis: true },
              { title: 'W.rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 50, ellipsis: true },
              { title: 'P.Size', key: 'pipesize', width: 60, ellipsis: true, render: (_: any, r: any) => r.pipesize_name || r.pipesize || '' },
              { title: 'Class', key: 'serviceclass', width: 60, ellipsis: true, render: (_: any, r: any) => r.serviceclass_name || r.serviceclass || '' },
              { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 60, ellipsis: true },
              { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 70, ellipsis: true },
              { title: 'F/S', key: 'weld_category', width: 40, ellipsis: true, render: (_: any, r: any) => r.weld_category || r.weld_category_name || '' },
              { title: 'Type', key: 'weldtype', width: 50, ellipsis: true, render: (_: any, r: any) => r.weldtype_name || r.weldtype || '' },
              { title: 'Code', dataIndex: 'code_no', key: 'code_no', width: 50, ellipsis: true },
            ] as any}
            dataSource={displayWeldRows}
            rowKey={(r: any) => r.key}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
              columnWidth: 24,
            }}
            style={{ width: '100%' }}
          />
        </div>

        <Form.Item label={<b>Subject</b>} name="rfi_subject" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>

        <Form.Item label={<b>Notes</b>} name="rfi_notes" style={{ marginBottom: 8 }}>
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />
            <Button action="add" type="primary" htmlType="submit">Issue RFI Request</Button>
          </div>
        </Form.Item>
      </Form>

      {/* Modals */}
      <PersonnelSelect
        open={persModalOpen}
        onCancel={() => setPersModalOpen(false)}
        onOk={(val: any) => {
          let fullName: string | undefined;
          let pid: string | undefined;
          if (Array.isArray(val)) {
            fullName = val[2];
            pid = String(val[0] ?? '');
          } else if (typeof val === 'string') {
            const s = val.trim();
            const dash = s.indexOf(' - ');
            if (dash >= 0) {
              pid = s.slice(0, dash).trim();
              fullName = s.slice(dash + 3).trim();
            } else {
              fullName = s;
            }
          }
          form.setFieldsValue({ issued_by: fullName, issued_by_id: pid });
          setPersModalOpen(false);
        }}
        mode="name_only"
      />

      <PersonnelSelect
        open={recModalOpen}
        onCancel={() => setRecModalOpen(false)}
        onOk={(val: any) => {
          const arr = Array.isArray(val) ? val : [val];
          const names: string[] = [];
          const codes: string[] = [];
          arr.forEach((s: any) => {
            const str = String(s);
            const idx = str.indexOf(' - ');
            if (idx >= 0) {
              codes.push(str.slice(0, idx).trim());
              names.push(str.slice(idx + 3).trim());
            } else {
              names.push(str.trim());
            }
          });
          // Auto-include issuer in recipient_ids
          const issuerPid: string | undefined = form.getFieldValue('issued_by_id');
          if (issuerPid && !codes.includes(String(issuerPid))) codes.push(String(issuerPid));
          form.setFieldsValue({ recipients: names, recipient_ids: codes, recipients_display: names.join('; ') });
          setRecModalOpen(false);
        }}
        multi
      />

      <WPermitSelect
        open={wpModalOpen}
        value={form.getFieldValue('work_permit_ref_num')}
        onCancel={() => setWpModalOpen(false)}
        onOk={([refNum, desc]) => {
          form.setFieldsValue({ work_permit_ref_num: refNum, work_permit_description: desc });
          setWpModalOpen(false);
        }}
      />

      <WeldSelectModal
        open={weldModalOpen}
        mode={weldModalMode}
        value={selectedWelds}
        returnAllModes
        onCancel={() => setWeldModalOpen(false)}
        onOk={(vals) => {
          const payload = (vals && (vals as any).modes) ? (vals as any) : { modes: { basic: vals as WeldSelectValue[], full: undefined } };
          const basic: WeldSelectValue[] = payload.modes.basic || [];
          const full: any[] | undefined = payload.modes.full;
          const toNumRev = (v: any): number => {
            if (v === null || v === undefined || v === '') return -Infinity;
            const n = Number(v);
            return Number.isFinite(n) ? n : -Infinity;
          };
          if (weldModalMode === 'add') {
            // 1) Merge BASIC keeping highest rev per weldingdata_id (synchronously)
            const mergedBasicSource = [...selectedWelds, ...basic];
            const byIdBasic = new Map<string, WeldSelectValue>();
            let conflicts = 0;
            mergedBasicSource.forEach(w => {
              const id = String(w.weldingdata_id);
              const existing = byIdBasic.get(id);
              if (!existing) {
                byIdBasic.set(id, w);
              } else {
                conflicts += 1;
                const curRev = toNumRev(existing.weld_no_rev);
                const newRev = toNumRev(w.weld_no_rev);
                if (newRev > curRev) byIdBasic.set(id, w);
              }
            });
            const newBasic = Array.from(byIdBasic.values());
            if (conflicts > 0) message.info('Multiple revisions selected for some welds. Kept highest revision.');
            setSelectedWelds(newBasic);

            // 2) Merge FULL to mirror BASIC (if provided)
            if (full && Array.isArray(full)) {
              const prevById = new Map<string, any>();
              selectedWeldDetails.forEach((p: any) => prevById.set(String(p.weldingdata_id), p));
              const fullByKey = new Map<string, any>();
              full.forEach((f: any) => fullByKey.set(`${String(f.weldingdata_id)}|${String(f.weld_no_rev)}`, f));
              const byIdFull = new Map<string, any>();
              newBasic.forEach(w => {
                const id = String(w.weldingdata_id);
                const existing = byIdFull.get(id) || prevById.get(id);
                if (!existing) {
                  const f = fullByKey.get(`${id}|${String(w.weld_no_rev)}`) || prevById.get(id);
                  if (f) byIdFull.set(id, f);
                  else byIdFull.set(id, { weldingdata_id: id, weld_no_rev: w.weld_no_rev });
                } else {
                  const curRev = toNumRev(existing.weld_no_rev);
                  const newRev = toNumRev(w.weld_no_rev);
                  if (newRev > curRev) {
                    // Prefer fresh full; if missing, fall back to previous details instead of empty
                    const f = fullByKey.get(`${id}|${String(w.weld_no_rev)}`) || prevById.get(id) || { weldingdata_id: id, weld_no_rev: w.weld_no_rev };
                    byIdFull.set(id, f);
                  } else {
                    // Keep existing details
                    byIdFull.set(id, existing);
                  }
                }
              });
              setSelectedWeldDetails(Array.from(byIdFull.values()));
            } else {
              // No full provided (e.g., from Pending modal). Keep previous details for existing ids
              // and ensure placeholders exist for new ids to keep display rows aligned.
              const prevById = new Map<string, any>();
              selectedWeldDetails.forEach((p: any) => prevById.set(String(p.weldingdata_id), p));
              const next: any[] = [];
              newBasic.forEach(w => {
                const id = String(w.weldingdata_id);
                const prev = prevById.get(id);
                if (prev) next.push(prev);
                else next.push({ weldingdata_id: id, weld_no_rev: w.weld_no_rev });
              });
              setSelectedWeldDetails(next);
            }
          } else {
            // Remove by id from both BASIC and FULL, only if something selected
            const removeList = basic || [];
            if (!removeList.length) {
              message.info('Select welds to remove');
            } else {
              const removeIds = new Set(removeList.map(v => String(v.weldingdata_id)));
              setSelectedWelds(prev => prev.filter(w => !removeIds.has(String(w.weldingdata_id))));
              setSelectedWeldDetails(prev => prev.filter((w: any) => !removeIds.has(String(w.weldingdata_id))));
            }
          }
          setWeldModalOpen(false);
        }}
      />

    </Card>
  );
};

export default QaqcPipeRfiRequest;
